/**
 * An offer to start something, and the request it turns into (UC-B4).
 *
 * The employee asked for leave rather than asking about it, so the assistant offers to prepare the
 * request. See `docs/adr/0007-admin-defined-actions.md`.
 *
 * ## Declining has to be one click, and nothing is lost by it
 *
 * Intent matching will sometimes be wrong. The ADR makes that cheap on purpose: the offer arrives
 * *beside* the answer, never instead of it, so an employee who asked a question and was offered a
 * form still has their answer underneath. Dismissing removes the card and leaves the turn intact.
 *
 * That is why this is a card and not a mode. An assistant that switched into a form would have to
 * be switched back out of, and getting it wrong would cost the employee the thing they came for.
 *
 * ## Submitting is a button an employee presses, or it does not happen
 *
 * ADR 0007 §4. Where the bank has connected the system, the review step ends in Submit and the
 * platform makes the call; where it has not, the card ends at a completed request the employee
 * copies into the portal themselves. `submits` on the offer says which, and the review reads
 * differently in each case rather than showing a button that would fail.
 *
 * Either way there is no path from a model's output to a bank system. The assistant collects and
 * shows; a person decides.
 *
 * ## The rules belong to the server
 *
 * Nothing here decides whether a value is acceptable. Every field is sent to be checked and the
 * problems come back attached to their fields — because a rule evaluated in the browser is a rule
 * evaluated twice, and the two will disagree the day somebody edits the feature.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  Button,
  Input,
  Select,
  Textarea,
  cx,
  startDraft,
  submitDraft,
  updateDraft,
  useLanguage,
  type FeatureDraft,
  type FeatureFieldSpecPublic,
  type FeatureOffer,
} from "@nab/shared";

import { CopyButton } from "./CopyButton";

export interface FeatureCardProps {
  offer: FeatureOffer;
  /** The conversation this came from, so the draft can be found again from the transcript. */
  sessionId: string | null;
  employeeId?: string;
}

type Stage = "offered" | "collecting" | "ready" | "submitted" | "dismissed";

export function FeatureCard({ offer, sessionId, employeeId }: FeatureCardProps): JSX.Element | null {
  const { t } = useTranslation("app");
  const { t: common } = useTranslation();
  const { language } = useLanguage();

  const [stage, setStage] = useState<Stage>("offered");
  const [draft, setDraft] = useState<FeatureDraft | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // The definition this draft was started against, not today's. An administrator editing the
  // feature must not change the form under someone who is halfway through filling it in.
  const fields = draft?.feature.fields ?? offer.fields;
  const problems = draft?.problems ?? [];
  const problemFor = (key: string): string | undefined =>
    problems.find((problem) => problem.field === key)?.message;

  if (stage === "dismissed") return null;

  async function begin(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      const started = await startDraft(
        offer.key,
        { session_id: sessionId, language, values: {} },
        employeeId ? { employeeId } : {},
      );
      setDraft(started);
      setStage("collecting");
    } catch (caught) {
      setFailure(
        caught instanceof ApiError && typeof caught.detail === "string"
          ? caught.detail
          : t("feature.startFailed"),
      );
    } finally {
      setBusy(false);
    }
  }

  async function check(): Promise<void> {
    if (!draft) return;
    setBusy(true);
    setFailure(null);
    try {
      const checked = await updateDraft(
        draft.draft_id,
        values,
        employeeId ? { employeeId } : {},
      );
      setDraft(checked);
      // The server's own verdict, not a count of empty inputs here. See the module comment.
      setStage(checked.status === "ready" ? "ready" : "collecting");
    } catch (caught) {
      setFailure(
        caught instanceof ApiError && typeof caught.detail === "string"
          ? caught.detail
          : t("feature.checkFailed"),
      );
    } finally {
      setBusy(false);
    }
  }

  async function send(): Promise<void> {
    if (!draft) return;
    setBusy(true);
    setFailure(null);
    try {
      const sent = await submitDraft(draft.draft_id, employeeId ? { employeeId } : {});
      setDraft(sent);
      setStage("submitted");
    } catch (caught) {
      // The platform's own sentence, which is written for the employee: "the request timed out
      // before the system answered. It may still have been received — check the portal before
      // sending it again." Replacing that with "submission failed" would be worse than useless,
      // because the thing they need to know is whether to try again.
      setFailure(
        caught instanceof ApiError && typeof caught.detail === "string"
          ? caught.detail
          : t("feature.submitFailed"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      // `region` with a name: the card is a distinct thing inside a turn, and a screen-reader user
      // arriving at an answer should be told there is an offer attached to it rather than hearing
      // a form appear mid-sentence.
      aria-label={t("feature.regionLabel", { name: offer.name })}
      className="flex flex-col gap-3 rounded-lg border border-accentBorder bg-accentSubtle p-3"
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex-1">
          <h3 className="text-label-md text-textPrimary">
            <bdi>{offer.name}</bdi>
          </h3>
          <p className="mt-1 text-body-sm text-textMuted">
            <bdi>{offer.description}</bdi>
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => setStage("dismissed")}>
          {stage === "offered" ? t("feature.notNow") : common("actions.close")}
        </Button>
      </header>

      {stage === "offered" ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="primary" size="sm" disabled={busy} onClick={() => void begin()}>
            {busy ? t("feature.starting") : t("feature.start")}
          </Button>
          <p className="text-body-sm text-textMuted">{t("feature.offerNote")}</p>
        </div>
      ) : null}

      {stage === "collecting" ? (
        <form
          className="flex flex-col gap-3"
          // `noValidate` and `required` together, deliberately. The attribute stays so a screen
          // reader announces which fields are needed; native validation is off because it would
          // block submission before the server ever saw the request — and the server is the only
          // thing that decides what is missing. Without this the browser silently refuses to
          // submit, in its own language, for rules the feature never declared.
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void check();
          }}
        >
          {fields.map((field) => (
            <FieldInput
              key={field.key}
              field={field}
              value={values[field.key] ?? ""}
              {...(problemFor(field.key) ? { error: problemFor(field.key)! } : {})}
              onChange={(next) => setValues((current) => ({ ...current, [field.key]: next }))}
            />
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" variant="primary" size="sm" disabled={busy}>
              {busy ? t("feature.checking") : t("feature.check")}
            </Button>
            {problems.length > 0 ? (
              // The count, not the sentences: each sentence is already beside the field it is
              // about, and repeating them here would make the employee match them up twice.
              <p className="text-body-sm text-statusDanger" role="status">
                {t("feature.problems", { count: problems.length })}
              </p>
            ) : null}
          </div>
        </form>
      ) : null}

      {(stage === "ready" || stage === "submitted") && draft ? (
        <ReadyRequest
          draft={draft}
          offer={offer}
          busy={busy}
          submitted={stage === "submitted"}
          onSubmit={() => void send()}
        />
      ) : null}

      {failure ? (
        <p role="alert" className="text-body-sm text-statusDanger">
          {failure}
        </p>
      ) : null}
    </section>
  );
}

/**
 * The completed request, field by field, exactly as it would be filed.
 *
 * ADR 0007 §4: the employee sees what would be sent before anything is. In M1.5 nothing is sent at
 * all, so this doubles as the thing they copy into the portal — which is most of the value of the
 * feature to the person filling it in.
 */
function ReadyRequest({
  draft,
  offer,
  busy,
  submitted,
  onSubmit,
}: {
  draft: FeatureDraft;
  offer: FeatureOffer;
  busy: boolean;
  submitted: boolean;
  onSubmit: () => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  const valueText = useValueText();

  const lines = draft.feature.fields
    .filter((field) => draft.values[field.key] !== undefined && draft.values[field.key] !== "")
    .map((field) => `${field.label}: ${valueText(field, draft.values[field.key])}`);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-borderSubtle bg-surfaceRaised p-3">
      <h4 className="text-label-md text-textPrimary">{t("feature.readyHeading")}</h4>
      <dl className="flex flex-col gap-1">
        {draft.feature.fields.map((field) => (
          <div key={field.key} className="flex flex-wrap gap-2">
            <dt className="text-body-sm text-textMuted">
              <bdi>{field.label}</bdi>
            </dt>
            <dd className="text-body-sm text-textPrimary">
              <bdi>{valueText(field, draft.values[field.key])}</bdi>
            </dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-wrap items-center gap-2">
        {/* Copy stays available after submitting: the employee still wants the request in an email
            or a ticket, and a reference alone is not the request. */}
        <CopyButton text={`${draft.feature.name}\n${lines.join("\n")}`} />
        {offer.submits && !submitted ? (
          <Button type="button" variant="primary" size="sm" disabled={busy} onClick={onSubmit}>
            {busy ? t("feature.submitting") : t("feature.submit")}
          </Button>
        ) : null}
      </div>

      {submitted ? (
        <div className="rounded-sm border border-statusInfo bg-statusInfoSubtle p-3">
          <p className="text-body-sm text-textPrimary">{t("feature.submittedHeading")}</p>
          {draft.external_reference ? (
            // The one thing that makes this chaseable. Selectable, monospaced and forced LTR: it
            // is a reference number the employee will read out or paste into an email.
            <p className="mt-1 select-all font-mono text-body-md text-textPrimary" dir="ltr">
              {draft.external_reference}
            </p>
          ) : (
            <p className="mt-1 text-body-sm text-textMuted">{t("feature.noReference")}</p>
          )}
        </div>
      ) : (
        <p className="text-body-sm text-textMuted">
          {/* Which world this feature is in, said plainly. Nothing has been sent in either. */}
          {offer.submits ? t("feature.willBeFiled") : t("feature.submitYourself")}
        </p>
      )}
    </div>
  );
}

/**
 * What to show for a stored value.
 *
 * A choice's stored value is stable and its label is the employee's, so the review shows the
 * label — an employee checking their own request should not have to recognise `unpaid_leave`.
 */
function useValueText(): (field: FeatureFieldSpecPublic, value: unknown) => string {
  const { t } = useTranslation("app");
  return (field, value) => {
    if (value === undefined || value === null || value === "") return "—";
    if (field.type === "choice") {
      const option = field.options.find(([stored]) => stored === String(value));
      return option ? option[1] : String(value);
    }
    if (field.type === "boolean") {
      return String(value) === "true" ? t("feature.yes") : t("feature.no");
    }
    return String(value);
  };
}

function FieldInput({
  field,
  value,
  error,
  onChange,
}: {
  field: FeatureFieldSpecPublic;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  const shared = {
    label: field.label,
    value,
    ...(error ? { error } : {}),
    ...(field.help_text ? { hint: field.help_text } : {}),
    required: field.required,
  };

  if (field.type === "choice") {
    return (
      <Select
        {...shared}
        onChange={(event) => onChange(event.target.value)}
        options={[
          { value: "", label: "—" },
          ...field.options.map(([stored, shown]) => ({ value: stored, label: shown })),
        ]}
      />
    );
  }

  if (field.type === "boolean") {
    // A select rather than a checkbox: an unticked box and a question not yet answered look the
    // same, and "did you not need it, or have you not got there yet" is exactly what the required
    // check on the server is trying to tell the difference between.
    return (
      <Select
        {...shared}
        onChange={(event) => onChange(event.target.value)}
        options={[
          { value: "", label: "—" },
          { value: "true", label: t("feature.yes") },
          { value: "false", label: t("feature.no") },
        ]}
      />
    );
  }

  if (field.type === "long_text") {
    return <Textarea {...shared} rows={3} onChange={(event) => onChange(event.target.value)} />;
  }

  return (
    <Input
      {...shared}
      // `date` and `number` get the browser's own control, which is also what gives an Arabic
      // interface a date picker in its own calendar rather than a free-text field to mistype into.
      type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
      className={cx(field.type === "date" || field.type === "number" ? "max-w-xs" : undefined)}
      {...(field.type === "date" || field.type === "number" ? { dir: "ltr" } : {})}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
